import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User, AuthError } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ success: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  isDemo: boolean;
  useDemoAuth: () => void;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Demo credentials for backwards compatibility
const DEMO_EMAIL = 'demo@traqpack.com';
const DEMO_PASSWORD = 'traqpack2024';

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        // Handle PKCE recovery/login codes on ANY route, then redirect to /reset-password
        const params = new URLSearchParams(window.location.search);
        // With implicit flow, Supabase will detect session from hash automatically
        const hasCode = params.get('code');
        if (hasCode) {
          const newUrl = `${window.location.origin}/reset-password`;
          window.history.replaceState({}, '', newUrl);
        }

        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        
        // Check if this is the demo user
        if (session?.user?.email === DEMO_EMAIL) {
          setIsDemo(true);
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setIsDemo(session?.user?.email === DEMO_EMAIL);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      // The trigger on auth.users table will automatically create the user_profile
      // No need to manually update it here

      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Signup error:', authError);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to sign up';
      if (authError.message.includes('duplicate key') || authError.message.includes('already registered')) {
        errorMessage = 'An account with this email already exists';
      } else if (authError.message.includes('password')) {
        errorMessage = 'Password must be at least 6 characters';
      } else if (authError.message) {
        errorMessage = authError.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      return { 
        success: false, 
        error: authError.message || 'Invalid credentials' 
      };
    }
  };

  const signOut = async () => {
    try {
      console.log('Starting signOut process...'); // Debug
      
      // Clear local state first
      setUser(null);
      setIsDemo(false);
      
      // Then sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase signOut error:', error);
        throw error;
      }
      
      console.log('SignOut completed successfully'); // Debug
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if Supabase signOut fails, we've cleared local state
      // so the user will see the login screen
    }
  };

  const useDemoAuth = async () => {
    // Try to sign in with demo credentials
    const result = await signIn(DEMO_EMAIL, DEMO_PASSWORD);
    if (!result.success) {
      // If demo user doesn't exist, create it
      const signUpResult = await signUp(DEMO_EMAIL, DEMO_PASSWORD, 'Demo User');
      if (signUpResult.success) {
        await signIn(DEMO_EMAIL, DEMO_PASSWORD);
      }
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      return { 
        success: true, 
        message: 'Password reset instructions have been sent to your email' 
      };
    } catch (error: any) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to send password reset email' 
      };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Password update error:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update password' 
      };
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated: !!user, 
        isLoading,
        signUp,
        signIn,
        signOut,
        isDemo,
        useDemoAuth,
        resetPassword,
        updatePassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};